/**
 * <notify-me-modal> — Klaviyo back-in-stock popup.
 *
 * Opened by product-form.js when a selected-but-sold-out variant's submit button
 * is clicked:
 *   document.dispatchEvent(new CustomEvent('notify-me:open', { detail: { variantId } }))
 *
 * Reads the current variant from variant-selects[data-selected-variant] (kept in sync
 * by product-info.js on every variant change), populates the product card, and POSTs
 * to Klaviyo's public client back-in-stock endpoint using the public key exposed via
 * window.altoKlaviyo (set from theme settings — public key only).
 */
if (!customElements.get('notify-me-modal')) {
  customElements.define(
    'notify-me-modal',
    class NotifyMeModal extends HTMLElement {
      // Pinned Klaviyo API revision (date-based). Bump deliberately when adopting a
      // newer API version — not editor-facing.
      static REVISION = '2024-10-15';
      static ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';

      constructor() {
        super();
        this.openerElement = null;
        this.variantId = null;

        this.panel = this.querySelector('[role="dialog"]') || this.firstElementChild;
        this.form = this.querySelector('[data-notify-form]');
        this.emailInput = this.querySelector('input[name="notify_email"]');
        this.emailError = this.querySelector('[data-error-for="notify_email"]');
        this.status = this.querySelector('[data-notify-status]');
        this.submitButton = this.querySelector('[data-notify-submit]');
        this.submitLabel = this.submitButton?.querySelector('span');

        this.imageEl = this.querySelector('[data-notify-image]');
        this.titleEl = this.querySelector('[data-notify-title]');
        this.sizeEl = this.querySelector('[data-notify-size]');

        this.sizeOptionIndex = parseInt(this.dataset.sizeOptionIndex ?? '-1', 10);

        // Localized text + product title come from a JSON blob (see snippet). Shopify's
        // `t` filter HTML-escapes apostrophes (' -> &#39;), so decode entities before we
        // assign these via textContent — otherwise the raw entity shows on screen.
        let config = {};
        try {
          config = JSON.parse(this.querySelector('[data-notify-config]')?.textContent || '{}');
        } catch {
          config = {};
        }
        this.productTitle = this.decodeEntities(config.productTitle);
        this.sizeLabel = this.decodeEntities(config.sizeLabel) || 'Size';
        this.successText = this.decodeEntities(config.successText);
        this.errorText = this.decodeEntities(config.errorText);

        this.onKeydown = this.onKeydown.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.handleOpen = this.handleOpen.bind(this);
      }

      connectedCallback() {
        document.addEventListener('notify-me:open', this.handleOpen);

        this.querySelectorAll('[data-notify-close]').forEach((btn) =>
          btn.addEventListener('click', () => this.close())
        );

        // Backdrop click (clicks on the host, outside the panel)
        this.addEventListener('click', (evt) => {
          if (evt.target === this) this.close();
        });

        this.form?.addEventListener('submit', this.onSubmit);

        // Clear the inline email error as the user corrects it
        this.emailInput?.addEventListener('input', () => this.clearEmailError());
      }

      disconnectedCallback() {
        document.removeEventListener('notify-me:open', this.handleOpen);
      }

      // Decode HTML entities (e.g. &#39; -> ') so strings escaped by Liquid's `t` filter
      // display correctly when assigned via textContent.
      decodeEntities(value) {
        if (!value) return '';
        const el = document.createElement('textarea');
        el.innerHTML = value;
        return el.value;
      }

      handleOpen(evt) {
        // Scope to the variant-selects this modal belongs to (set in Liquid). With one
        // product per page there's a single picker, but this keeps quick-add safe.
        const variantId = evt.detail?.variantId;
        this.open(variantId, document.activeElement);
      }

      getCurrentVariant() {
        const selector = this.dataset.variantSelects
          ? `#${this.dataset.variantSelects} [data-selected-variant]`
          : 'variant-selects [data-selected-variant]';
        const node =
          document.querySelector(selector) || document.querySelector('variant-selects [data-selected-variant]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch {
          return null;
        }
      }

      populate(variant) {
        if (!variant) return;

        // Image: variant-specific, else keep the product fallback already in the DOM
        const src = variant.featured_image?.src || variant.featured_media?.preview_image?.src;
        if (src && this.imageEl) {
          this.imageEl.src = src;
          this.imageEl.alt = variant.featured_image?.alt || this.productTitle || '';
          this.imageEl.hidden = false;
        }

        if (this.titleEl && this.productTitle) {
          this.titleEl.textContent = this.productTitle;
        }

        // The color/options line is rendered server-side from the product's
        // `custom.barva_produkta` metafield — leave it as-is. Only the size line
        // is variant-specific, so update it from the selected variant's options.
        const options = Array.isArray(variant.options) ? variant.options : [];
        if (this.sizeEl) {
          if (this.sizeOptionIndex >= 0 && options.length > this.sizeOptionIndex) {
            this.sizeEl.textContent = `${this.sizeLabel}: ${options[this.sizeOptionIndex]}`;
            this.sizeEl.hidden = false;
          } else {
            this.sizeEl.hidden = true;
          }
        }
      }

      open(variantId, opener) {
        this.openerElement = opener instanceof HTMLElement ? opener : null;

        const variant = this.getCurrentVariant();
        // Prefer the variant id passed by the trigger; fall back to the JSON's id.
        this.variantId = variantId || variant?.id || null;

        this.populate(variant);
        this.resetForm();

        this.removeAttribute('hidden');
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');
        document.addEventListener('keydown', this.onKeydown);

        // global.js helper — moves focus into the panel and tracks the opener
        if (typeof trapFocus === 'function') {
          trapFocus(this.panel, this.emailInput);
        } else {
          this.emailInput?.focus();
        }
      }

      close() {
        this.setAttribute('hidden', '');
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        document.removeEventListener('keydown', this.onKeydown);

        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(this.openerElement);
        } else {
          this.openerElement?.focus();
        }
      }

      onKeydown(evt) {
        if (evt.key === 'Escape') {
          evt.preventDefault();
          this.close();
        }
      }

      resetForm() {
        this.form?.reset();
        this.clearEmailError();
        this.setStatus(null);
        this.setLoading(false);
      }

      clearEmailError() {
        if (!this.emailError) return;
        this.emailError.textContent = '';
        this.emailError.hidden = true;
        this.emailInput?.removeAttribute('aria-invalid');
      }

      showEmailError(message) {
        if (!this.emailError) return;
        this.emailError.textContent = message;
        this.emailError.hidden = false;
        this.emailInput?.setAttribute('aria-invalid', 'true');
        this.emailInput?.focus();
      }

      setStatus(message, type) {
        if (!this.status) return;
        if (!message) {
          this.status.hidden = true;
          this.status.textContent = '';
          this.status.classList.remove('tw:text-green-500', 'tw:text-red-500');
          return;
        }
        this.status.textContent = message;
        this.status.hidden = false;
        this.status.classList.toggle('tw:text-green-500', type === 'success');
        this.status.classList.toggle('tw:text-red-500', type === 'error');
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        this.submitButton.toggleAttribute('disabled', isLoading);
        this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      }

      async onSubmit(evt) {
        evt.preventDefault();
        this.setStatus(null);

        const email = (this.emailInput?.value || '').trim();
        if (!email || !this.emailInput.checkValidity()) {
          this.showEmailError(this.emailInput?.validationMessage || 'Please enter a valid email address.');
          return;
        }

        const config = window.altoKlaviyo || {};
        if (!config.publicKey) {
          console.error('[notify-me] Missing Klaviyo public key — set it in Theme settings → Klaviyo.');
          this.setStatus(this.errorText, 'error');
          return;
        }
        if (!this.variantId) {
          console.error('[notify-me] No variant id available for subscription.');
          this.setStatus(this.errorText, 'error');
          return;
        }

        this.setLoading(true);

        const body = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${this.variantId}`,
                },
              },
            },
          },
        };

        try {
          const url = `${NotifyMeModal.ENDPOINT}?company_id=${encodeURIComponent(config.publicKey)}`;
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: NotifyMeModal.REVISION,
            },
            body: JSON.stringify(body),
          });

          // The client endpoint returns 202 Accepted with an empty body on success.
          if (response.ok || response.status === 202) {
            this.form.reset();
            this.setStatus(this.successText, 'success');
          } else {
            const detail = await response.text().catch(() => '');
            console.error('[notify-me] Klaviyo error', response.status, detail);
            this.setStatus(this.errorText, 'error');
          }
        } catch (error) {
          console.error('[notify-me] Request failed', error);
          this.setStatus(this.errorText, 'error');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}
